import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Share2,
  Wallet,
  CheckCircle2,
  TrendingUp,
  MousePointerClick,
  Copy,
  RefreshCw,
  Package,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { publicApi, formatPrecio, formatPeriodo } from "@/lib/agenda";

export default function AffiliatePortal() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  const load = useCallback(async () => {
    try {
      const d = await publicApi.affiliatePortal(code);
      setData(d);
      setStatus("ok");
    } catch (e) {
      setStatus("error");
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const refLink = `${window.location.origin}/?ref=${code}`;
  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      toast.success("Enlace copiado.");
    } catch {
      toast.error("No se pudo copiar. Cópialo a mano.");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center warm-canvas">
        <p className="text-sm text-slate-500">Cargando tu portal...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center warm-canvas px-6">
        <div className="text-center">
          <h1 className="font-heading text-xl font-bold text-slate-900">Enlace no válido</h1>
          <p className="text-sm text-slate-500 mt-2">No encontramos este afiliado. Revisa tu enlace o pide uno nuevo.</p>
        </div>
      </div>
    );
  }

  const { affiliate, sales, commissions, clicks, pending, paid } = data;

  return (
    <div className="min-h-screen warm-canvas" data-testid="affiliate-portal">
      <header className="border-b border-[#EDE7E0] bg-white/70 backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#275FBE]"
            data-testid="portal-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center h-11 w-11 rounded-2xl text-white" style={{ backgroundColor: "#2E2A8F" }}>
            <Share2 className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Hola, {affiliate.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5">Este es tu portal. Aquí ves tus ventas y comisiones al día.</p>
          </div>
        </div>

        {/* Tu enlace */}
        <div className="bg-white rounded-2xl border border-[#EAE5DF] p-5">
          <p className="text-sm font-medium text-slate-900 mb-2">Tu enlace para compartir</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 truncate text-slate-600" data-testid="portal-ref-link">{refLink}</code>
            <button onClick={copyRef} className="inline-flex items-center gap-1 text-xs font-medium text-[#275FBE] border border-[#275FBE] rounded-lg px-2.5 py-2 hover:bg-[#275FBE] hover:text-white transition-colors shrink-0" data-testid="portal-copy-ref">
              <Copy className="h-3.5 w-3.5" /> Copiar
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={MousePointerClick} label="Clics en tu enlace" value={clicks} />
          <Stat icon={TrendingUp} label="Ventas" value={sales.length} />
          <Stat icon={Wallet} label="Pendiente de cobro" value={formatPrecio(pending)} tone="pending" />
          <Stat icon={CheckCircle2} label="Ya cobrado" value={formatPrecio(paid)} tone="paid" />
        </div>

        {/* Comisiones */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Tus comisiones</h2>
          {commissions.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no tienes comisiones. En cuanto entre una venta, aparecerá aquí.</p>
          ) : (
            <div className="bg-white rounded-2xl border border-[#EAE5DF] overflow-hidden" data-testid="portal-commissions">
              {commissions.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{formatPrecio(c.amount)}</p>
                    <p className="text-xs text-slate-500">{c.product_name} · {formatPeriodo(c.period)}</p>
                  </div>
                  <span className={`text-xs font-medium px-3 py-1.5 rounded-full border shrink-0 ${c.status === "pagada" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                    {c.status === "pagada" ? "Pagada" : "Pendiente"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ventas */}
        {sales.length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Tus ventas</h2>
            <div className="space-y-2" data-testid="portal-sales">
              {sales.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-[#EAE5DF] p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5 text-[#2E2A8F]" /> {s.product_name}
                    </p>
                    <p className="text-xs text-slate-500">{s.start_date} · {s.pay_type === "mensual" ? "mensual" : "único"}{s.pay_type === "mensual" && !s.active ? " · dado de baja" : ""}</p>
                  </div>
                  <span className="text-sm font-medium text-slate-700 shrink-0">{formatPrecio(s.commission_each)}{s.pay_type === "mensual" ? "/mes" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-[#EDE7E0] py-5 text-center text-xs text-slate-500">
        Agenda Terapias · Programa de afiliados
      </footer>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone }) => {
  const bg = tone === "paid" ? "#ECFDF5" : tone === "pending" ? "#FFFBEB" : "#EAF1FB";
  const color = tone === "paid" ? "#047857" : tone === "pending" ? "#B45309" : "#275FBE";
  return (
    <div className="bg-white rounded-2xl border border-[#EAE5DF] p-4 shadow-[0_6px_24px_-14px_rgba(46,42,143,0.10)]">
      <span className="flex items-center justify-center h-9 w-9 rounded-xl" style={{ backgroundColor: bg, color }}>
        <Icon className="h-4.5 w-4.5" />
      </span>
      <p className="mt-3 text-xs text-slate-500">{label}</p>
      <p className="font-heading text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
};
